<script setup lang="ts">
  import {
    ICreateOptionDto,
    IUpdateOptionDto,
    IUpdateOptionItemDto,
  } from '@/api/services/options.service';
  import TranslationsService, {
    IOptionTranslationEntry,
  } from '@/api/services/translations.service';
  import V2TranslationEntryRow, {
    ITranslationEntryModel,
  } from '@/components/modals/translations/V2TranslationEntryRow.vue';
  import { ILanguage } from '@/interfaces/ILanguage';
  import { IOption } from '@/interfaces/IOption';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { useUi } from '@/composition/use-ui';
  import { vueData } from '@/VueData';
  import {
    computed,
    getCurrentInstance,
    inject,
    onMounted,
    Ref,
    ref,
  } from 'vue';

  const optionToEdit = inject<Ref<IOption | null>>('optionToEdit');
  /** Live editor state: reflects in-session edits/deletions before they persist. */
  const optionData = inject<Ref<ICreateOptionDto | IUpdateOptionDto>>(
    'optionData'
  );

  const restaurantStore = useRestaurantStore();
  const ui = useUi();
  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string): string {
    return String(proxy.$t(key));
  }

  const translationsService = new TranslationsService();

  const languages = computed<ILanguage[]>(
    () => vueData.restaurantLanguages ?? []
  );

  /**
   * Group row plus one row per persisted choice. Derived from the LIVE editor
   * data so choices deleted in this session drop out immediately (unsaved new
   * choices carry local negative ids and are excluded until they persist).
   */
  const targets = computed(() => {
    const option = optionToEdit.value;
    if (!option?.id) return [];

    const liveItems = ((optionData.value.optionItems ??
      []) as IUpdateOptionItemDto[]).filter(
      (oI) => !!oI.id && oI.id > 0 && !oI.deletedAt
    );

    return [
      {
        optionItemId: null as number | null,
        label: optionData.value.title || option.title,
      },
      ...liveItems.map((oI) => ({
        optionItemId: oI.id as number | null,
        label: oI.title,
      })),
    ];
  });

  function entryKey(optionItemId: number | null, languageId: string) {
    return `${languageId}|${optionItemId ?? 'group'}`;
  }

  const entries = ref<Record<string, ITranslationEntryModel> | null>(null);
  let originalEntries: Record<string, ITranslationEntryModel> = {};

  onMounted(() => {
    loadTranslations();
  });

  async function loadTranslations() {
    const option = optionToEdit.value;
    if (!option?.id || languages.value.length === 0) {
      entries.value = {};
      return;
    }

    try {
      const rows = await translationsService.getOptionTranslations(
        restaurantStore.restaurant.id,
        option.id
      );

      // Build every (target, language) model upfront so v-model stays reactive.
      const map: Record<string, ITranslationEntryModel> = {};
      for (const lang of languages.value) {
        for (const target of targets.value) {
          map[entryKey(target.optionItemId, lang.id)] = {
            title: null,
            description: null,
            outdated: false,
          };
        }
      }

      for (const row of rows) {
        const parsed = parseKey(row.key);
        if (!parsed) continue;

        const model = map[entryKey(parsed.optionItemId, row.languageId)];
        if (!model) continue;

        if (parsed.field === 'title') model.title = row.value;
        else model.description = row.value;
        if (row.outdated) model.outdated = true;
      }

      originalEntries = JSON.parse(JSON.stringify(map));
      entries.value = map;
    } catch (e) {
      ui.serverError(e, i18nT('optionEditor.translationsLoadError'));
      entries.value = {};
    }
  }

  /** Parses `o{id}-title` / `oi{id}-desc` keys into target + field. */
  function parseKey(
    key: string
  ): { optionItemId: number | null; field: 'title' | 'description' } | null {
    const match = /^(o|oi)(\d+)-(title|desc)$/.exec(key);
    if (!match) return null;

    return {
      optionItemId: match[1] === 'oi' ? Number(match[2]) : null,
      field: match[3] === 'title' ? 'title' : 'description',
    };
  }

  /** Lazy-save contract: called by V2ModalOption after the option persists. */
  async function save(option: IOption) {
    if (!entries.value || !option?.id) return;

    const normalize = (value: string | null | undefined) =>
      value?.trim() ? value.trim() : null;

    // Guard against choices that no longer exist in the just-saved option:
    // the API rejects the whole batch if any entry targets a deleted choice.
    const savedAliveIds = new Set(
      (option.optionItems ?? [])
        .filter((oI) => !oI.deletedAt)
        .map((oI) => oI.id)
    );

    const changed: IOptionTranslationEntry[] = [];
    for (const lang of languages.value) {
      for (const target of targets.value) {
        if (
          target.optionItemId !== null &&
          !savedAliveIds.has(target.optionItemId)
        ) {
          continue;
        }

        const key = entryKey(target.optionItemId, lang.id);
        const current = entries.value[key];
        const original = originalEntries[key];
        if (!current) continue;

        const title = normalize(current.title);
        const description = normalize(current.description);
        if (
          title === normalize(original?.title) &&
          description === normalize(original?.description)
        ) {
          continue;
        }

        changed.push({
          optionItemId: target.optionItemId,
          languageId: lang.id,
          title,
          description,
        });
      }
    }

    if (changed.length === 0) return;

    await translationsService.saveOptionTranslations(
      restaurantStore.restaurant.id,
      option.id,
      changed
    );
  }

  defineExpose({
    save,
  });
</script>

<template>
  <div class="option-translations">
    <template v-if="!optionToEdit || !optionToEdit.id">
      <p class="info-message">
        {{ $t('optionEditor.translationsSaveFirst') }}
      </p>
    </template>
    <template v-else-if="languages.length === 0">
      <p class="info-message">
        {{ $t('optionEditor.translationsNoLanguages') }}
      </p>
    </template>
    <template v-else-if="!entries">
      <v-skeleton-loader type="paragraph" />
    </template>
    <template v-else>
      <p class="hint">{{ $t('optionEditor.translationsHint') }}</p>
      <div v-for="lang of languages" :key="lang.id" class="language-section">
        <span class="language-name">{{ lang.name }}</span>
        <div class="language-entries">
          <V2TranslationEntryRow
            v-for="target of targets"
            :key="entryKey(target.optionItemId, lang.id)"
            v-model="entries[entryKey(target.optionItemId, lang.id)]"
            :language="lang"
            :label="target.label"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .option-translations {
    @apply tw-flex tw-flex-col tw-gap-4 tw-pt-3;

    .info-message {
      @apply tw-mb-0 tw-rounded-lg tw-bg-neutral-100 tw-px-3 tw-py-2 tw-text-sm tw-text-neutral-500;
    }

    .hint {
      @apply tw-mb-0 tw-text-xs tw-text-neutral-500;
    }

    .language-section {
      @apply tw-flex tw-flex-col tw-gap-2;

      .language-name {
        @apply tw-text-sm tw-font-semibold tw-text-neutral-900;
      }

      .language-entries {
        @apply tw-flex tw-flex-col tw-gap-2;
      }
    }
  }
</style>
